// FILE: src/components/Admin/AI/rag/ragAdminConstants.ts
import type { TuningField } from '../tuning/TuningAdminTable';

const option = (value: string, label?: string) => ({
  value,
  label: label || value,
});

export const scopeTypeOptions = [
  option('product', 'Product'),
  option('product_feature', 'Product Feature'),
  option('product_component', 'Product Component'),
  option('product_use_case', 'Product Use Case'),
  option('go_to_market', 'Go-To-Market'),
  option('solution_component', 'Solution Component'),
  option('security_use_case', 'Security Use Case'),
  option('security_use_case_component', 'Security Use Case Component'),
  option('product_offer_type', 'Product Offer Type Default'),
  option('go_to_market_type', 'Go-To-Market Type Default'),
  option('global', 'Global Default'),
];

export const entityTypeOptions = [
  'product',
  'product_feature',
  'product_component',
  'product_use_case',
  'go_to_market',
  'solution_component',
  'security_use_case',
  'security_use_case_component',
].map(value => option(value));

export const queueTaskTypeOptions = [
  option('product_documentation_generation', 'Product Documentation Generation'),
  option('entity_documentation_generation', 'Entity Documentation Generation'),
];

export const snackableQueueTaskTypeOptions = [
  option('snackable_course_generation', 'Snackable Course Generation'),
];

export const ingestionStatusOptions = [
  option('pending', 'Pending'),
  option('running', 'Running'),
  option('succeeded', 'Succeeded'),
  option('failed', 'Failed'),
  option('cancelled', 'Cancelled'),
];

export const triggerSourceOptions = [
  option('scheduled', 'Scheduled'),
  option('manual', 'Manual'),
  option('doc_export', 'Doc Export'),
  option('api', 'API'),
];

export const targetTypeOptions = [
  option('product', 'Product'),
  option('go_to_market', 'Go-To-Market'),
  option('global', 'Global'),
];

export const RoutingMappingFields: TuningField[] = [
  { name: 'scope_type', label: 'Scope Type', type: 'select', options: scopeTypeOptions, required: true },
  { name: 'scope_id', label: 'Scope ID', type: 'number' },
  { name: 'entity_type', label: 'Entity Type ID', type: 'number' },
  { name: 'purpose', label: 'Purpose ID', type: 'number' },
  { name: 'pipeline_group', label: 'Pipeline Group ID', type: 'number', required: true },
  { name: 'priority', label: 'Priority', type: 'number' },
  { name: 'is_default', label: 'Default', type: 'boolean' },
  { name: 'is_active', label: 'Active', type: 'boolean' },
  { name: 'meta_filters', label: 'Meta Filters', type: 'json', minRows: 8 },
  { name: 'notes', label: 'Notes', type: 'textarea', minRows: 3 },
  { name: 'updated_by', label: 'Updated By' },
];

export const EntityTypeFields: TuningField[] = [
  { name: 'key', label: 'Key', type: 'select', required: true, options: entityTypeOptions },
  { name: 'label', label: 'Label', required: true },
  { name: 'description', label: 'Description', type: 'textarea', minRows: 3 },
  { name: 'supports_entity_documentation', label: 'Supports Entity Docs', type: 'boolean' },
  { name: 'supports_snackable_course', label: 'Supports Snackable Course', type: 'boolean' },
  { name: 'queue_task_type', label: 'Queue Task Type', type: 'select', options: queueTaskTypeOptions },
  {
    name: 'snackable_queue_task_type',
    label: 'Snackable Queue Task Type',
    type: 'select',
    options: snackableQueueTaskTypeOptions,
  },
  { name: 'canonical_model_name', label: 'Canonical Model Name' },
  { name: 'is_active', label: 'Active', type: 'boolean' },
  { name: 'sort_order', label: 'Sort Order', type: 'number' },
  { name: 'updated_by', label: 'Updated By' },
];

export const EntityAliasFields: TuningField[] = [
  { name: 'entity_type', label: 'Entity Type ID', type: 'number', required: true },
  { name: 'alias', label: 'Alias', required: true },
  { name: 'updated_by', label: 'Updated By' },
];

export const ProductOfferTypeFields: TuningField[] = [
  { name: 'key', label: 'Key', required: true },
  { name: 'label', label: 'Label', required: true },
  { name: 'description', label: 'Description', type: 'textarea', minRows: 3 },
  { name: 'is_active', label: 'Active', type: 'boolean' },
  { name: 'sort_order', label: 'Sort Order', type: 'number' },
  { name: 'updated_by', label: 'Updated By' },
];

export const GtmTypeFields: TuningField[] = [
  { name: 'key', label: 'Key', required: true },
  { name: 'label', label: 'Label', required: true },
  { name: 'description', label: 'Description', type: 'textarea', minRows: 3 },
  { name: 'is_active', label: 'Active', type: 'boolean' },
  { name: 'sort_order', label: 'Sort Order', type: 'number' },
  { name: 'updated_by', label: 'Updated By' },
];

export const PurposeFields: TuningField[] = [
  { name: 'key', label: 'Key', required: true },
  { name: 'label', label: 'Label', required: true },
  { name: 'description', label: 'Description', type: 'textarea', minRows: 3 },
  { name: 'is_active', label: 'Active', type: 'boolean' },
  { name: 'sort_order', label: 'Sort Order', type: 'number' },
  { name: 'updated_by', label: 'Updated By' },
];

export const IngestionRunFields: TuningField[] = [
  { name: 'pipeline_group', label: 'Pipeline Group ID', type: 'number', required: true },
  { name: 'pipeline', label: 'Pipeline ID', type: 'number' },
  { name: 'pipeline_slot', label: 'Legacy Pipeline Slot ID', type: 'number' },
  { name: 'target_type', label: 'Target Type', type: 'select', options: targetTypeOptions },
  { name: 'product', label: 'Product ID', type: 'number' },
  { name: 'go_to_market', label: 'Go-To-Market ID', type: 'number' },
  { name: 'status', label: 'Status', type: 'select', options: ingestionStatusOptions },
  { name: 'trigger_source', label: 'Trigger Source', type: 'select', options: triggerSourceOptions },
  { name: 'job_id', label: 'Job ID' },
  { name: 'execution_id', label: 'Execution ID' },
  { name: 'manifest_s3_bucket', label: 'Manifest S3 Bucket' },
  { name: 'manifest_s3_key', label: 'Manifest S3 Key', type: 'textarea', minRows: 2 },
  { name: 'uploaded_files_count', label: 'Uploaded Files', type: 'number' },
  { name: 'deleted_files_count', label: 'Deleted Files', type: 'number' },
  { name: 'skipped_files_count', label: 'Skipped Files', type: 'number' },
  { name: 'request_payload', label: 'Request Payload', type: 'json', minRows: 8 },
  { name: 'response_payload', label: 'Response Payload', type: 'json', minRows: 8 },
  { name: 'error_message', label: 'Error Message', type: 'textarea', minRows: 4 },
  { name: 'updated_by', label: 'Updated By' },
];